import {
  HEC_SRC_CAMPAIGN_KEYS,
  HEC_SRC_CLICK_ID_KEYS,
  HEC_SRC_COOKIE_NAME,
  HEC_SRC_MAX_AGE_SECONDS,
  HEC_SRC_MAX_FIELD_LENGTH,
  HEC_SRC_MAX_VALUE_LENGTH,
  HEC_SRC_OWN_DOMAINS,
} from "./hec-src";

/**
 * `hec_src` entry-attribution cookie — client-side inline backstop
 * (SFCC-12091).
 *
 * The server middleware write (`hec-src.server.ts`) is skipped whenever the
 * production edge cache serves the document (`CacheMiddleware` sits outside
 * `SessionManagementMiddleware`, and Set-Cookie is stripped on cache put) —
 * and shared campaign URLs are exactly the documents that cache-HIT. Cached
 * HTML still carries this script, so the capture happens client-side there.
 * It also reads `document.referrer`, the only referrer signal available on a
 * cached landing.
 *
 * The snippet mirrors the server builder's four write semantics EXACTLY —
 * `hec-src-inline.test.ts` executes this shipped string against the shared
 * serializer and fails on any byte divergence. Change them together.
 *
 * Rendered inside the contiguous body-top inline-script run (see root.tsx):
 * before the GTM container so the cookie exists before any GTM variable reads
 * it. It has no `dataLayer` dependency. Prerender-gated: a Speculation-Rules
 * prerender executes inline JS, and (like the middleware's `isPrefetch` gate)
 * a page the user never activated must not plant/refresh attribution — defer
 * to `prerenderingchange`. Fully static output → hydration-safe; whole body
 * try/catch → fail-silent.
 */
export function buildHecSrcInlineJs(): string {
  const name = JSON.stringify(HEC_SRC_COOKIE_NAME);
  const keys = JSON.stringify(HEC_SRC_CAMPAIGN_KEYS);
  const clickIds = JSON.stringify(HEC_SRC_CLICK_ID_KEYS);
  const ownDomains = JSON.stringify(HEC_SRC_OWN_DOMAINS);
  const maxAge = String(HEC_SRC_MAX_AGE_SECONDS);
  const fieldCap = String(HEC_SRC_MAX_FIELD_LENGTH);
  const valueCap = String(HEC_SRC_MAX_VALUE_LENGTH);
  return (
    "(function(){" +
    `var N=${name},KEYS=${keys},CLICK=${clickIds},OWN=${ownDomains},MAXAGE=${maxAge},FCAP=${fieldCap},VCAP=${valueCap};` +
    // COM-510 mirror of isClickIdOnlyCampaign.
    "function clickOnly(c){var i,k,hc=false,hu=false;" +
    "for(i=0;i<KEYS.length;i++){k=KEYS[i];if(!c[k])continue;if(CLICK.indexOf(k)>-1)hc=true;else hu=true;}" +
    "return hc&&!hu;}" +
    "function reg(h){var p=h.toLowerCase().split('.').filter(function(x){return x;});return p.slice(-2).join('.');}" +
    "function own(h){var r=location.hostname.toLowerCase();h=h.toLowerCase();" +
    "if(!h||h===r)return true;var d=reg(h);" +
    "return d===reg(r)||OWN.indexOf(d)>-1;}" +
    "function readRaw(){var m=document.cookie.match(new RegExp('(?:^|;\\\\s*)'+N+'=([^;]*)'));return m?m[1]:null;}" +
    "function parse(raw){if(!raw)return null;var out={},pairs=raw.split('&'),i,eq;" +
    "for(i=0;i<pairs.length;i++){eq=pairs[i].indexOf('=');if(eq<=0)continue;" +
    "try{out[pairs[i].slice(0,eq)]=decodeURIComponent(pairs[i].slice(eq+1));}catch(e){return null;}}" +
    "var ts=Number(out.ts);if(!isFinite(ts)||ts<=0)return null;" +
    "var any=!!out.ref;for(i=0;i<KEYS.length;i++)if(out[KEYS[i]])any=true;" +
    "return any?out:null;}" +
    "function build(f,ref){var pairs=[],i,v;" +
    "for(i=0;i<KEYS.length;i++){v=f[KEYS[i]];if(v)pairs.push(KEYS[i]+'='+encodeURIComponent(String(v).slice(0,FCAP)));}" +
    "if(ref)pairs.push('ref='+encodeURIComponent(String(ref).slice(0,FCAP)));" +
    "pairs.push('ts='+Math.floor(f.ts));return pairs.join('&');}" +
    "function serialize(f){var v=build(f,f.ref);" +
    "if(v.length>VCAP&&f.ref){var o;try{o=new URL(f.ref).origin;}catch(e){o=undefined;}" +
    "v=build(f,o);if(v.length>VCAP)v=build(f,undefined);}return v;}" +
    "function write(v){document.cookie=N+'='+v+'; path=/; max-age='+MAXAGE+'; samesite=lax'+(location.protocol==='https:'?'; secure':'');}" +
    "function run(){try{" +
    "var sp=new URLSearchParams(location.search),campaign=null,i,v;" +
    "for(i=0;i<KEYS.length;i++){v=sp.get(KEYS[i]);if(v){campaign=campaign||{};campaign[KEYS[i]]=v;}}" +
    "var raw=readRaw(),ext=null;" +
    "if(document.referrer){try{var ru=new URL(document.referrer);" +
    "if((ru.protocol==='http:'||ru.protocol==='https:')&&!own(ru.hostname))ext=document.referrer;}catch(e){}}" +
    "if(campaign){var existing=parse(raw),same=false;" +
    "if(existing){same=true;for(i=0;i<KEYS.length;i++){" +
    "var a=(existing[KEYS[i]]||'').slice(0,FCAP),b=(campaign[KEYS[i]]||'').slice(0,FCAP);" +
    "if(a!==b){same=false;break;}}}" +
    "if(same){write(raw);return;}" +
    // COM-510 mirror of the server merge branch: a click-id-only URL with no
    // external referrer is Conversion Linker decoration on an internal nav,
    // so fold the click id into the stored value and keep utm_*/ref/ts.
    "if(existing&&!ext&&clickOnly(campaign)){var m={},mk;" +
    "for(mk in existing)if(Object.prototype.hasOwnProperty.call(existing,mk))m[mk]=existing[mk];" +
    "for(i=0;i<CLICK.length;i++)if(campaign[CLICK[i]])m[CLICK[i]]=campaign[CLICK[i]];" +
    "m.ts=Number(existing.ts);write(serialize(m));return;}" +
    "campaign.ts=Date.now();if(ext)campaign.ref=ext;" +
    "write(serialize(campaign));return;}" +
    "if(raw&&parse(raw)){write(raw);return;}" +
    "if(ext)write(serialize({ref:ext,ts:Date.now()}));" +
    "}catch(e){}}" +
    "try{if(document.prerendering){document.addEventListener('prerenderingchange',run,{once:true});}else{run();}}catch(e){}" +
    "})();"
  );
}
